import { Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import {
  IconEdit,
  IconPlayerPlay,
  IconPlayerStop,
  IconTrash,
} from "@tabler/icons-react";
import { productApi } from "apis/productApi";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { STATUS } from "constants/common/common";
import { CommonParamsType } from "constants/types/common";
import { ProductResponseType } from "constants/types/product";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FORM_IDS } from "pages/Product/config";
import { FC, useMemo, useRef, useCallback } from "react";
import { useNotification } from "utils/hooks";
import {
  useProducts,
  getProductListKey,
} from "utils/hooks/request/useProducts";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { useSWRConfig } from "swr";
import { Loading } from "components";
import EditForm from "../EditForm/EditForm";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

interface ProductTableProps {
  params: CommonParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(
    value,
  );

const ProductTable: FC<ProductTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const { notification } = useNotification();
  const { mutate } = useSWRConfig();
  const ability = useAbility();

  const {
    products,
    pagination,
    isLoading,
    mutate: localMutate,
  } = useProducts(params);

  const { openCrudModal, closeAll, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleDeleteProduct = useCallback(
    async (productId: string) => {
      try {
        await productApi.delete(productId);
        notification.success({ title: "Xóa mặt hàng thành công" });
        closeAll();
        localMutate();
        onPageChange(1);
      } catch {
        notification.error({ title: "Xóa mặt hàng thất bại" });
      }
    },
    [notification, closeAll, localMutate, onPageChange],
  );

  const handleOpenDeleteModal = useCallback(
    (productId: string) => {
      openCrudModal(
        <CustomText variant="section-title">Xác nhận xóa mặt hàng</CustomText>,
        {
          mode: "delete",
          formId: FORM_IDS.DELETE,
          onConfirmDelete: async () => await handleDeleteProduct(productId),
        },
      );
    },
    [openCrudModal, handleDeleteProduct],
  );

  const handleToggleActive = useCallback(
    async (product: ProductResponseType) => {
      const nextActive = !product.isActive;
      try {
        // PUT là PartialType nên gửi mỗi isActive là đủ, không cần dựng lại
        // toàn bộ body mặt hàng.
        await productApi.update(product.id, { isActive: nextActive });
        notification.success({
          title: nextActive
            ? "Đã cho mặt hàng hoạt động trở lại"
            : "Đã ngưng hoạt động mặt hàng",
        });
        closeAll();
        localMutate();
      } catch {
        notification.error({
          title: nextActive
            ? "Cho hoạt động trở lại thất bại"
            : "Ngưng hoạt động thất bại",
        });
      }
    },
    [notification, closeAll, localMutate],
  );

  const handleOpenToggleActiveModal = useCallback(
    (product: ProductResponseType) => {
      const stopping = product.isActive;
      modals.openConfirmModal({
        title: stopping
          ? "Ngưng hoạt động mặt hàng"
          : "Cho mặt hàng hoạt động trở lại",
        children: (
          <Text size="sm">
            {stopping ? (
              <>
                Mặt hàng{" "}
                <Text component="span" fw={600}>
                  {product.name}
                </Text>{" "}
                sẽ không còn hiển thị ở màn hình tồn kho. File Excel tồn kho vẫn
                xuất đầy đủ như cũ.
              </>
            ) : (
              <>
                Mặt hàng{" "}
                <Text component="span" fw={600}>
                  {product.name}
                </Text>{" "}
                sẽ hiển thị trở lại ở màn hình tồn kho.
              </>
            )}
          </Text>
        ),
        labels: {
          confirm: stopping ? "Ngưng hoạt động" : "Cho hoạt động",
          cancel: "Hủy",
        },
        confirmProps: { color: stopping ? "red" : "green" },
        onConfirm: () => handleToggleActive(product),
      });
    },
    [handleToggleActive],
  );

  const handleOpenEditModal = useCallback(
    (product: ProductResponseType) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Chỉnh sửa mặt hàng</CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.EDIT,
          children: (
            <EditForm
              product={product}
              mutate={() => mutate(getProductListKey(params))}
              onClose={handleCloseModal}
            />
          ),
          submitText: "Cập nhật",
        },
        750,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const productActions: ActionItem<ProductResponseType>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "orange",
        permission: "product:update",
        onClick: (row) => handleOpenEditModal(row),
      },
      // ActionItem.label/icon là giá trị tĩnh, không nhận hàm theo dòng — nên
      // tách hai nút và dùng hidden() để mỗi dòng chỉ hiện đúng một cái.
      {
        key: "deactivate",
        label: "Ngưng hoạt động",
        icon: <IconPlayerStop size={ICON_SIZE} />,
        color: "gray",
        permission: "product:update",
        hidden: (row) => !row.isActive,
        onClick: (row) => handleOpenToggleActiveModal(row),
      },
      {
        key: "activate",
        label: "Cho hoạt động trở lại",
        icon: <IconPlayerPlay size={ICON_SIZE} />,
        color: "green",
        permission: "product:update",
        hidden: (row) => row.isActive,
        onClick: (row) => handleOpenToggleActiveModal(row),
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "product:delete",
        onClick: (row) => handleOpenDeleteModal(row.id),
      },
    ],
    [handleOpenEditModal, handleOpenToggleActiveModal, handleOpenDeleteModal],
  );

  const hasActions = useMemo(
    () =>
      productActions.some((a) =>
        canAccessRequirement(ability, a.permission, true),
      ),
    [productActions, ability],
  );

  const columns: MRT_ColumnDef<ProductResponseType>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã mặt hàng",
    },
    {
      accessorKey: "name",
      header: "Tên mặt hàng",
    },
    // {
    //   accessorKey: "spec",
    //   header: "Mô tả",
    //   grow: false,
    //   size: 300,
    // },
    {
      accessorKey: "unit.name",
      header: "Đơn vị tính",
    },
    {
      accessorKey: "category.name",
      header: "Nhóm hàng hóa",
    },
    {
      accessorKey: "priceNet",
      header: "Giá Net",
      Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
    },
    {
      accessorKey: "isActive",
      header: "Trạng thái",
      grow: false,
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <CustomBadge label={STATUS.DANG_SU_DUNG} color="green" />
        ) : (
          <CustomBadge label={STATUS.KHONG_SU_DUNG} color="gray" />
        ),
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 110,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={productActions} />
            ),
          } satisfies MRT_ColumnDef<ProductResponseType>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={products ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default ProductTable;
